import type { SimulationState, ThemeData } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

export function financeSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  _rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];
  const ticksPerWeek = themeData.balance.ticksPerDay * themeData.balance.daysPerWeek;
  const isWeekBoundary = state.company.currentTick % ticksPerWeek === 0 && state.company.currentTick > 0;

  if (!isWeekBoundary) return events;

  let weeklyExpenses = 0;
  let weeklyIncome = 0;

  // Deduct salaries
  for (const staff of state.staff) {
    if (staff.salary > 0) {
      weeklyExpenses += staff.salary;
    }
  }

  // Deduct rent
  weeklyExpenses += state.office.weeklyRent;

  // Product revenue
  for (const product of state.products) {
    const weeksSinceRelease =
      (state.company.currentTick / ticksPerWeek) -
      ((product.releaseYear - 1) * themeData.balance.weeksPerYear + product.releaseWeek - 1);

    if (weeksSinceRelease < 0 || weeksSinceRelease > themeData.balance.salesDurationWeeks) continue;

    // Calculate this week's sales using decay curve
    const peakSales = product.weeklySales[0] ?? 0;
    const weeklySales = Math.round(
      peakSales * Math.exp(-themeData.balance.salesDecayRate * weeksSinceRelease),
    );

    if (weeklySales > 0) {
      weeklyIncome += weeklySales;
      product.weeklySales.push(weeklySales);
      product.totalSales += weeklySales;
      product.totalRevenue += weeklySales;
    }
  }

  // Loan repayments
  let loanPayments = 0;
  const paidOffLoans: string[] = [];
  if (!state.loans) state.loans = [];
  for (const loan of state.loans) {
    const payment = Math.min(loan.weeklyPayment, loan.remaining);
    loan.remaining -= payment;
    loan.totalPaid += payment;
    loan.weeksLeft--;
    loanPayments += payment;
    if (loan.remaining <= 0 || loan.weeksLeft <= 0) {
      paidOffLoans.push(loan.id);
    }
  }
  if (paidOffLoans.length > 0) {
    state.loans = state.loans.filter((l) => !paidOffLoans.includes(l.id));
  }
  weeklyExpenses += loanPayments;

  // Apply expenses
  state.company.funds -= weeklyExpenses;
  state.company.totalExpenses += weeklyExpenses;

  // Apply income
  state.company.funds += weeklyIncome;
  state.company.totalRevenue += weeklyIncome;

  if (weeklyExpenses > 0) {
    events.push({
      type: 'FUNDS_CHANGED',
      newFunds: state.company.funds,
      delta: weeklyIncome - weeklyExpenses,
      reason: `Weekly: +$${weeklyIncome} income, -$${weeklyExpenses} expenses`,
    });
  }

  // Debt tracking
  if (state.company.funds < 0) {
    state.company.weeksInDebt++;

    // Interest on negative balance
    const interestRate = state.company.isHardMode
      ? themeData.balance.hardMode.debtInterestRate
      : themeData.balance.debtInterestRate;
    const interest = Math.round(Math.abs(state.company.funds) * interestRate);
    if (interest > 0) {
      state.company.funds -= interest;
      state.company.totalExpenses += interest;
      events.push({
        type: 'DEBT_INTEREST',
        amount: interest,
        weeksInDebt: state.company.weeksInDebt,
      });
    }

    // Game over check
    if (state.company.weeksInDebt >= themeData.balance.gameOverDebtWeeks) {
      events.push({
        type: 'GAME_OVER',
        year: state.company.currentYear,
        week: state.company.currentWeek,
        finalScore: 0, // Calculated by PrestigeSystem
      });
    }
  } else {
    state.company.weeksInDebt = 0;
  }

  // Low funds warning
  const weeklyBurn = weeklyExpenses - weeklyIncome;
  if (weeklyBurn > 0 && state.company.funds > 0) {
    const weeksOfRunway = Math.floor(state.company.funds / weeklyBurn);
    if (weeksOfRunway <= themeData.balance.lowFundsWarningWeeks) {
      events.push({
        type: 'LOW_FUNDS_WARNING',
        funds: state.company.funds,
        weeksOfRunway,
      });
    }
  }

  return events;
}
